import * as actions from "../actions/hotels";
import * as moment from "moment";
import {cloneDeep, isEqual} from "lodash";
import {Hotel, HotelListingAvailability, Search, destinations} from "../models/hotels";
import {
    HOTEL_API_ERROR,
    HOTEL_CHECK_DISCOUNT_CODE,
    HOTEL_DISCOUNT_LOADING,
    HOTEL_GET_TICKET_PRICE,
    HOTEL_RESET_ORDER,
    HOTEL_UPDATE_SEARCH,
    HOTEL_UPDATE_VALIDATION_ERROR
} from "../actions/hotels";
import {DiscountCode, Order, OrderTicket, OrderTicketPriceInfo} from "../models/order";
import {HOTEL_UPDATE_DISCOUNT_CODE} from "../actions/hotels";
import {HOTEL_GET_TICKET_PRICE_LOADING} from "../actions/hotels";
import {HOTEL_PAUSE_ORDERING} from "../actions/hotels";
import {HOTEL_SET_TICKET_PRICE_INFO} from "../actions/hotels";
import {localStorageGet} from "../utils";

export interface HotelFilters {
    name?: string;
    available?: boolean;
    offer?: string;
}

export interface HotelState {
    loading: boolean;
    searchLoading: boolean;
    orderLoading: boolean;
    errorMessage?: string;
    searchCriteria: Search;
    searchCriteriaForResults?: Search;
    searchResults: HotelListingAvailability[];
    filteredResults: HotelListingAvailability[];
    hotel?: Hotel;
    staleResults: boolean;
    editAgesOpen: boolean;
    order?: Order;
    filters: HotelFilters;
    validationError?: string;
    newDiscountCode: string;
    newDiscountSuccess?: boolean;
    discountCodeMessage?: string;
    discountCodeLoading: boolean;
    orderTicketInProcess?: OrderTicket;
    orderTicketLoading: boolean;
    orderTicketLoadingStart?: number;
    pauseOrdering: boolean;
    orderTicketPriceInfo?: OrderTicketPriceInfo;
}

const initialState = (): HotelState => {
    let searchCriteria: Search = null;
    const searchJson = localStorageGet(HOTEL_UPDATE_SEARCH);
    if (searchJson) {
        searchCriteria = JSON.parse(searchJson);
        searchCriteria.checkIn = moment(searchCriteria.checkIn);
        searchCriteria.checkOut = moment(searchCriteria.checkOut);
    }
    if (!searchCriteria) {
        searchCriteria = {
            destination: destinations[0].code,
            checkIn: moment().add(1, "week"),
            checkOut: moment().add(1, "week").add(3, "days"),
            numAdults: 2,
            childAges: [],
            numRooms: 1,
            includeTickets: true
        };
    }
    return {
        loading: false,
        orderLoading: false,
        searchLoading: false,
        searchCriteria,
        staleResults: true,
        searchResults: [],
        filteredResults: [],
        editAgesOpen: false,
        newDiscountCode: "",
        discountCodeLoading: false,
        orderTicketLoading: false,
        pauseOrdering: false,
        filters: {
            name: "",
            available: false,
            offer: ""
        }
    };
};

export function hotelReducer(state: HotelState = initialState(), action: actions.HotelAction): HotelState {
    switch (action.type) {
        case actions.HOTEL_SEARCH_LOADING:
            return {
                ...state,
                searchLoading: action.loading
            } as HotelState;
        case actions.HOTEL_LOADING:
            return {
                ...state,
                loading: action.loading
            } as HotelState;
        case actions.HOTEL_SEARCH_RESULTS:
            return {
                ...state,
                loading: false,
                searchCriteriaForResults: cloneDeep(state.searchCriteria),
                staleResults: false,
                searchResults: action.searchResults,
                filteredResults: filterHotels(action.searchResults, state.filters),
                order: action.order,
                errorMessage: null,
            } as HotelState;
        case actions.HOTEL_DETAIL:
            return {
                ...state,
                loading: false,
                searchCriteriaForResults: cloneDeep(state.searchCriteria),
                staleResults: false,
                order: action.order,
                hotel: action.hotel,
                errorMessage: null,
            } as HotelState;
        case actions.HOTEL_UPDATE_SEARCH:
            return {
                ...state,
                searchLoading: false,
                staleResults: !isEqual(action.searchCriteria, state.searchCriteriaForResults),
                searchCriteria: action.searchCriteria
            } as HotelState;
        case actions.HOTEL_EDIT_AGES:
            return {
                ...state,
                editAgesOpen: action.editAgesOpen && state.searchCriteria.childAges.length > 0
            } as HotelState;
        case actions.HOTEL_UPDATE_ORDER:
            return {
                ...state,
                order: action.order,
                orderLoading: false,
                validationError: null
            } as HotelState;
        case actions.HOTEL_ORDER_LOADING:
            return {
                ...state,
                orderLoading: action.loading,
            } as HotelState;
        case actions.HOTEL_UPDATE_FILTERS:
            return {
                ...state,
                filters: action.filters,
                filteredResults: filterHotels(state.searchResults, action.filters),
            } as HotelState;
        case HOTEL_API_ERROR:
            return {
                ...state,
                loading: false,
                errorMessage: action.errorMessage
            } as HotelState;
        case HOTEL_UPDATE_VALIDATION_ERROR:
            return {
                ...state,
                loading: false,
                validationError: action.validationError
            } as HotelState;
        case HOTEL_UPDATE_DISCOUNT_CODE:
            return {
                ...state,
                newDiscountCode: action.newDiscountCode
            };
        case HOTEL_DISCOUNT_LOADING:
            return {
                ...state,
                discountCodeLoading: action.discountCodeLoading
            } as HotelState;
        case HOTEL_CHECK_DISCOUNT_CODE:
            return {
                ...state,
                discountCodeLoading: false,
                discountCodeMessage: action.discountCodeMessage,
                newDiscountSuccess: action.newDiscountSuccess,
                newDiscountCode: action.newDiscountSuccess ? "" : state.newDiscountCode
            } as HotelState;
        case HOTEL_RESET_ORDER:
            return {
                ...state,
                loading: false,
                validationError: action.validationError,
                order: {}
            } as HotelState;
        case HOTEL_GET_TICKET_PRICE_LOADING:
            return {
                ...state,
                orderTicketLoading: action.orderTicketLoading,
                orderTicketLoadingStart: moment().valueOf()
            };
        case HOTEL_GET_TICKET_PRICE:
            return {
                ...state,
                orderTicketLoading: false,
                errorMessage: null,
                orderTicketInProcess: action.orderTicket,
            } as HotelState;
        case HOTEL_PAUSE_ORDERING:
            return  {
                ...state,
                pauseOrdering: action.pauseOrdering
            };
        case HOTEL_SET_TICKET_PRICE_INFO:
            return {
                ...state,
                orderTicketLoading: false,
                errorMessage: null,
                orderTicketPriceInfo: action.orderTicketPriceInfo,
                orderTicketInProcess: action.orderTicketPriceInfo.orderTicket
            };
        default:
            return state;
    }
}

function filterHotels(hotels: HotelListingAvailability[], filters: HotelFilters): HotelListingAvailability[] {
    if (! (filters.available || filters.name || filters.offer) ) {
        return hotels;
    }
    return hotels.filter(h => {
        if (filters.name && h.listing.title.toLowerCase().indexOf(filters.name) === -1) {
            return false;
        }
        if (filters.available && !h.availability) {
            return false;
        }
        return true;
    });
}



// WEBPACK FOOTER //
// ./src/reducers/hotels.ts